import {type SocketController} from './SocketController.ts'
import {NetworkEnums} from '../../../../shared/GameEnums.ts'
import State = NetworkEnums.State
import {type TimeoutId} from '@colonist/types'
import {cloneDeep} from 'lodash-es'

interface SocketMonitorStats {
    reconnects: number
    low: number
    medium: number
    high: number
    buckets: Record<number, number>
    dropped: number
}

const LATENCY_LOW_MS = 500
const LATENCY_MEDIUM_MS = 1_500
const LATENCY_HIGH_MS = 5_000

const PING_INTERVAL_MS = 1_000

export class SocketMonitor {
    private readonly controller: SocketController
    private timer: TimeoutId | undefined
    private readonly pingTimestamps: Set<number> = new Set()
    lastLatency: number = 0
    readonly stats: SocketMonitorStats
    private active: boolean = false

    constructor(controller: SocketController) {
        this.controller = controller
        this.stats = {
            reconnects: 0,
            low: 0,
            medium: 0,
            high: 0,
            dropped: 0,
            buckets: {},
        }

        this.controller.registerSessionEstablishedObserver(() => this.startMonitoringLatency())
        this.controller.registerSocketCloseObserver(() => this.stopMonitoringLatency())
        this.controller.setStateHandler(State.SocketMonitorUpdate, (data) => this.handlePong(data))
    }

    activate() {
        if(this.active) return

        this.active = true
        this.startMonitoringLatency()
    }

    private startMonitoringLatency() {
        if(!this.active) return
        if(this.timer != undefined) this.stats.reconnects += 1
        this.stopMonitoringLatency()

        this.timer = setInterval(() => this.sendPing(), PING_INTERVAL_MS)
    }

    private sendPing() {
        const ts = Date.now()
        this.pingTimestamps.add(ts)
        this.controller.sender.sendPing(ts)
    }

    private stopMonitoringLatency() {
        clearInterval(this.timer)
        this.pingTimestamps.clear()
    }

    private handlePong(data: Record<string, number>) {
        const pongTimestamp = Date.now()
        const pingTimestamp = data.timestamp
        if(!this.pingTimestamps.has(pingTimestamp)) return

        const latency = Math.round((pongTimestamp - pingTimestamp) / 2)
        this.lastLatency = latency

        const latencyBucket = getLatencyBucket(latency)

        this.stats.buckets[latencyBucket] ??= 0
        this.stats.buckets[latencyBucket] += 1

        if(latency <= LATENCY_LOW_MS) {
            this.stats.low += 1
        } else if(latency <= LATENCY_MEDIUM_MS) {
            this.stats.medium += 1
        } else if(latency <= LATENCY_HIGH_MS) {
            this.stats.high += 1
        } else {
            this.stats.dropped += 1
        }

        this.pingTimestamps.delete(pingTimestamp)
    }

    getFinalStats(): SocketMonitorStats {
        const stats = cloneDeep(this.stats)

        for(const pingTimestamp of this.pingTimestamps) {
            const latencyBucket = getLatencyBucket(Date.now() - pingTimestamp)

            stats.buckets[latencyBucket] ??= 0
            stats.buckets[latencyBucket] += 1
        }

        return stats
    }
}

function getLatencyBucket(latency: number): number {
    const size = 100
    return Math.round(latency / size) * size
}
